import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Clock, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

interface SessionTimeoutWarningProps {
  /** Whether the warning dialog is visible. */
  readonly open: boolean;
  /** Seconds remaining before automatic logout. */
  readonly remainingSeconds: number;
  /** Called when user chooses to extend the session. */
  readonly onExtend: () => void;
  /** Called when user chooses to log out immediately. */
  readonly onLogout: () => void;
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${seconds}s`;
}

export default function SessionTimeoutWarning({
  open,
  remainingSeconds,
  onExtend,
  onLogout,
}: SessionTimeoutWarningProps) {
  // Progress: 100% at start, 0% at logout
  const warningDuration = 120; // 2 minutes in seconds
  const progressPercent = Math.max(
    0,
    Math.min(100, (remainingSeconds / warningDuration) * 100),
  );

  const isUrgent = remainingSeconds <= 30;

  return (
    <AlertDialogPrimitive.Root open={open}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <AlertDialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] rounded-lg border bg-background p-6 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-full",
                isUrgent
                  ? "bg-destructive/10 text-destructive"
                  : "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
              )}
            >
              <Clock className="h-6 w-6" />
            </div>

            <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
              Session sur le point d&apos;expirer
            </AlertDialogPrimitive.Title>

            <AlertDialogPrimitive.Description className="text-sm text-muted-foreground max-w-xs">
              Votre session administrateur sera automatiquement fermee pour des raisons de securite.
            </AlertDialogPrimitive.Description>
          </div>

          {/* Countdown */}
          <div className="mt-5 flex flex-col items-center gap-2">
            <span
              className={cn(
                "text-3xl font-bold tabular-nums tracking-tight",
                isUrgent ? "text-destructive" : "text-foreground",
              )}
            >
              {formatTime(remainingSeconds)}
            </span>

            {/* Progress bar */}
            <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-1000 ease-linear",
                  isUrgent ? "bg-destructive" : "bg-amber-500",
                )}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Action asChild>
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-input bg-background px-4 text-sm font-medium text-muted-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <LogOut className="h-4 w-4" />
                Se deconnecter
              </button>
            </AlertDialogPrimitive.Action>

            <AlertDialogPrimitive.Cancel asChild>
              <button
                type="button"
                onClick={onExtend}
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Continuer la session
              </button>
            </AlertDialogPrimitive.Cancel>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
